import React, { Component } from 'react';
import { Divider, Header } from 'semantic-ui-react';
import { Row, FlexView, Text, Col } from '@cuemath/comps';
import styled from 'styled-components';
import { v4 as uuidV4 } from 'uuid';

import COLORS from 'constants/colors';
import SEMANTIC_COLORS from 'constants/semantic-colors';
import { ATTEMPT_LOCATION } from 'constants/node';
import { ReactComponent as StopWatchIcon } from 'img/stopwatch.svg';
import { labelBuilder } from 'utils';

import DependencySheetDetails from './dependency-sheet-details';

const SheetDetailsContainer = styled(FlexView)`
  opacity: ${({ showBlur }) => (showBlur ? 0.5 : 1)};
`;

const TextStyle = styled(Text)`
  max-width: 445px;
  overflow-wrap: break-word;
  margin-right: 5px;
`;

const FullTextContainer = styled.div`
  position: relative;
  padding-right: 2px;
  margin-right: ${({ dependency }) => (dependency ? '5px' : 'none')};
`;

const FullText = styled(Text)`
  display: none;
  box-shadow: 2px 2px 5px 2px rgba(0, 56, 122, 0.15);
  padding: ${({ dependency }) => (dependency ? '6px 0' : '6px 12px')};
  width: ${({ dependency }) => (dependency ? '550px' : 'fit-content')};
  border-radius: ${({ dependency }) => (dependency ? '6px' : '4px')};
  font-size: ${({ dependency }) => (dependency ? '14px' : '12px')};
  position: absolute;
  top: ${({ dependency }) => (dependency ? '22px' : 'calc(-100% - 12px)')};
  left: 0;
  right: ${({ dependency }) => !dependency && 0};
  z-index: 1;
  opacity: 0;
  background-color: ${COLORS.WHITE};
  transition: display 1s, opacity 1s ease-in-out;

  @media only screen and (min-device-width: 768px) and (max-device-width: 1024px) {
    top: ${({ dependency }) => !dependency && 'calc(-100% - 35px)'};
  }

  @media only screen and (min-device-width: 768px) and (max-device-width: 1024px) and (orientation: portrait) {
    top: ${({ dependency }) => !dependency && 'calc(-100% - 35px)'};
  }

  ${FullTextContainer}:hover & {
    display: block;
    opacity: 1;
  }
`;

const SheetText = styled(Text)`
  max-width: ${({ shouldReduceLength }) => (shouldReduceLength ? '340px' : '400px')};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  text-decoration: ${({ dependency }) => (dependency ? 'underline' : 'none')};
  cursor: ${({ dependency }) => dependency && 'pointer'};
`;

const TimePill = styled.div`
  background-color: ${COLORS.WHITE};
  border: 1px solid ${({ borderColor }) => borderColor || COLORS.SKY_DARK};
  padding: 0 8px;
  border-radius: 22px;
`;

const CenterText = styled(Text)`
  margin: 2px 0;
`;

const ERROR = 'error';

class SheetDetails extends Component {
  render() {
    const {
      workSheet,
      dependencyDataList,
      nodeDataList,
      attemptLocation,
      nodeType,
      position,
      timedSheet,
      linkedDataList,
      optionalSheet,
      completionData,
    } = this.props;
    const { learnosity_activity_ref, name } = workSheet || {};

    const atHome =
      attemptLocation === ATTEMPT_LOCATION.ANY ||
      attemptLocation === ATTEMPT_LOCATION.ATHOME;
    const inClass =
      attemptLocation === ATTEMPT_LOCATION.ANY ||
      attemptLocation === ATTEMPT_LOCATION.INCLASS;

    const { labelString: completionLabelString } = labelBuilder(completionData) || {};

    return (
      <SheetDetailsContainer>
        <Row alignItems="center">
          <FullTextContainer>
            <FullText weight="semibold">{name}</FullText>
            <SheetText weight="semibold" size="medium" color={COLORS.COLA}>
              {nodeType && (
                <Text display="inline" weight="bold" size="medium" color={COLORS.COLA}>
                  {nodeType}
                  {nodeType ? ' • ' : ''}
                </Text>
              )}
              {learnosity_activity_ref}
            </SheetText>
          </FullTextContainer>
          {inClass && (
            <Col gutter={2}>
              <TimePill>
                <Text size="smaller" color={COLORS.SKY_DARK}>
                  CW
                </Text>
              </TimePill>
            </Col>
          )}
          {atHome && (
            <Col gutter={2}>
              <TimePill borderColor={COLORS.COLA}>
                <Text size="smaller" color={COLORS.COLA}>
                  HW
                </Text>
              </TimePill>
            </Col>
          )}
          {timedSheet && <StopWatchIcon />}
          {optionalSheet && (
            <Col gutter={2}>
              <TimePill>
                <Text size="smaller" color={COLORS.SKY_DARK}>
                  OPTIONAL
                </Text>
              </TimePill>
            </Col>
          )}
        </Row>
        <Row>
          {linkedDataList && linkedDataList.length > 0 && linkedDataList[0].length > 0 && (
            <FullTextContainer dependency={true}>
              <SheetText
                dependency={true}
                weight="semibold"
                size="lighter"
                color={COLORS.COLA}
              >
                Dependency List
              </SheetText>
              <FullText dependency={true}>
                {linkedDataList.map(
                  (linkDataItem, index) =>
                    linkDataItem &&
                    linkDataItem.length > 0 && (
                      <Col alignItems="center" key={uuidV4()}>
                        <CenterText>
                          {index > 0 && (
                            <Divider horizontal>
                              <Header color={SEMANTIC_COLORS.YELLOW}>OR</Header>
                            </Divider>
                          )}
                        </CenterText>
                        <DependencySheetDetails
                          dependencyData={linkDataItem}
                          nodeDataList={nodeDataList}
                        />
                      </Col>
                    ),
                )}
              </FullText>
            </FullTextContainer>
          )}
          <TextStyle>{`${
            dependencyDataList && dependencyDataList.length ? ' |' : ''
          } Completion Criteria: ${completionLabelString || ERROR} |`}</TextStyle>
          <TextStyle>{` Position: ${position}`}</TextStyle>
        </Row>
      </SheetDetailsContainer>
    );
  }
}

export default SheetDetails;
